import React from "react";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import {
  createInsuranceCompany,
  listInsuranceCompanies,
  patchInsuranceCompany,
} from "../../api/insuranceCompanies";

const EMPTY_FORM = {
  name: "",
  claims_phone: "",
  claims_email: "",
  claims_address: "",
  website: "",
  notes: "",
};

export default function InsuranceCompaniesDirectory() {
  const [tab, setTab] = React.useState("directory");
  const [rows, setRows] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");

  const refresh = React.useCallback(async (search = query) => {
    setLoading(true);
    setError("");
    try {
      const items = await listInsuranceCompanies({ search, limit: 500, offset: 0 });
      setRows(Array.isArray(items) ? items : []);
    } catch (e) {
      setError(e?.message || "Failed to load insurance companies");
    } finally {
      setLoading(false);
    }
  }, [query]);

  React.useEffect(() => {
    refresh("");
  }, [refresh]);

  const onSearch = async (e) => {
    e.preventDefault();
    await refresh(query);
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!String(form.name || "").trim()) {
      setError("Company name is required.");
      return;
    }
    setBusy("create");
    setError("");
    setNotice("");
    try {
      await createInsuranceCompany(form);
      setForm(EMPTY_FORM);
      setNotice("Insurance company added.");
      await refresh(query);
      setTab("directory");
    } catch (err) {
      setError(err?.message || "Failed to add insurance company");
    } finally {
      setBusy("");
    }
  };

  const onQuickNoteSave = async (company) => {
    const id = String(company?.id || "");
    if (!id) return;
    setBusy(`note:${id}`);
    setError("");
    setNotice("");
    try {
      await patchInsuranceCompany(id, { notes: String(company.notes || "") });
      setNotice("Notes updated.");
      await refresh(query);
    } catch (err) {
      setError(err?.message || "Failed to update notes");
    } finally {
      setBusy("");
    }
  };

  const updateRowField = (id, key, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Insurance Company Contacts</h1>
          <p className="mt-1 text-sm text-slate-500">
            Claims phone, email, address, and notes in one searchable directory.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          {notice ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</p> : null}

          <div className="flex flex-wrap gap-2">
            <Button variant={tab === "directory" ? "primary" : "secondary"} onClick={() => setTab("directory")}>
              Directory
            </Button>
            <Button variant={tab === "add" ? "primary" : "secondary"} onClick={() => setTab("add")}>
              Add Company
            </Button>
          </div>

          {tab === "directory" ? (
            <div className="space-y-3">
              <form className="flex flex-wrap gap-2" onSubmit={onSearch}>
                <Input
                  className="min-w-[280px]"
                  placeholder="Search by company, phone, email, or address"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <Button type="submit" variant="secondary">Search</Button>
                <Button type="button" variant="ghost" onClick={() => { setQuery(""); refresh(""); }}>
                  Clear
                </Button>
              </form>
              {loading ? <p className="text-sm text-slate-500">Loading insurance companies...</p> : null}
              {!loading ? (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-slate-600">Company (A-Z)</th>
                        <th className="px-3 py-2 text-left text-slate-600">Claims Phone</th>
                        <th className="px-3 py-2 text-left text-slate-600">Claims Email</th>
                        <th className="px-3 py-2 text-left text-slate-600">Address</th>
                        <th className="px-3 py-2 text-left text-slate-600">Notes</th>
                        <th className="px-3 py-2 text-left text-slate-600">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length < 1 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-4 text-slate-500">No insurance companies found.</td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.id} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-900">{row.name}</td>
                            <td className="px-3 py-2 text-slate-700">{row.claims_phone || "-"}</td>
                            <td className="px-3 py-2 text-slate-700">{row.claims_email || "-"}</td>
                            <td className="px-3 py-2 text-slate-700">{row.claims_address || "-"}</td>
                            <td className="px-3 py-2 text-slate-700">
                              <Input
                                value={row.notes || ""}
                                placeholder="Notes"
                                onChange={(e) => updateRowField(row.id, "notes", e.target.value)}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={busy === `note:${row.id}`}
                                onClick={() => onQuickNoteSave(row)}
                              >
                                {busy === `note:${row.id}` ? "Saving..." : "Save"}
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : (
            <form className="grid gap-3 sm:grid-cols-2" onSubmit={onCreate}>
              <Input
                placeholder="Company name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              <Input
                placeholder="Claims phone"
                value={form.claims_phone}
                onChange={(e) => setForm((p) => ({ ...p, claims_phone: e.target.value }))}
              />
              <Input
                placeholder="Claims email"
                value={form.claims_email}
                onChange={(e) => setForm((p) => ({ ...p, claims_email: e.target.value }))}
              />
              <Input
                placeholder="Website"
                value={form.website}
                onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
              />
              <Input
                className="sm:col-span-2"
                placeholder="Claims address"
                value={form.claims_address}
                onChange={(e) => setForm((p) => ({ ...p, claims_address: e.target.value }))}
              />
              <Input
                className="sm:col-span-2"
                placeholder="Notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              />
              <div className="sm:col-span-2">
                <Button type="submit" disabled={busy === "create"}>
                  {busy === "create" ? "Saving..." : "Add Insurance Company"}
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

